import { useEffect, useState } from "react";
import { Box, Button } from "@mui/material";
import { RichContent } from "../editor/utils";
import { Attachment } from "protogen/common_pb";
import useIsMobile from "../hooks/useIsMobile";
import SimpleRichEditor from "../editor/SimpleRichEditor";
import useCached from "../hooks/useCached";
import { JSONContent } from "@tiptap/react";
import { StageValue } from "./workflowStates";
import StageChoiceButton from "./StageChoiceButton";
import FlexBox from "components/common/FlexBox";

export default ({
  onSaveDocument,
  isCreate,
  initialContent,
  initialAttachments,
  accountType = "advisor",
  isEditMode = false,
  onCancel = () => {},
  cacheKey = null,
  disabled = false,
  dropdownButtonId,
  taskStage,
}: {
  onSaveDocument: (
    content: RichContent | null,
    isShareable: boolean,
    newStatus?: string,
    holdDate?: string,
  ) => void;
  isCreate: boolean;
  initialContent?: string;
  initialAttachments?: Attachment[];
  accountType?: "advisor" | "member";
  isEditMode?: boolean;
  onCancel?: () => void;
  cacheKey?: string | null;
  hidePrimaryAction?: boolean;
  disabled?: boolean;
  dropdownButtonId?: string;
  taskStage: StageValue;
}) => {
  const isMobile = useIsMobile();
  const placeholder = "Comment";
  const [content, setContent] = useCached<RichContent | null>(cacheKey, null);
  const [_initialContent, setInitialContent] = useState<
    string | JSONContent | undefined
  >(
    initialContent ||
      (content?.text?.length && content?.json && JSON.parse(content?.json)) ||
      undefined,
  );
  useEffect(() => {
    setInitialContent(initialContent || undefined);
  }, [initialContent]);
  const [triggerReset, setTriggerReset] = useState(false);
  const [uploadsInProgress, setUploadsInProgress] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<StageValue | null>(null);

  const hasContent = !!(content?.text.length || content?.attachments.length);

  const handleSave = (newStatus?: string, stageHoldDate?: string) => {
    if (!content && !newStatus) {
      onCancel();
      return;
    }
    let updateContent = content;
    if (newStatus && !(content?.text.length || content?.attachments.length)) {
      updateContent = null;
    }
    onSaveDocument(updateContent, true, newStatus, stageHoldDate);
    if (isCreate) {
      setTriggerReset(true);
    }
    setSelectedStatus(null);
  };

  const handleStatusSelect = (value: StageValue, _holdDate?: string) => {
    // If the value is the same as the current task stage, reset to null (initial state)
    if (value === taskStage) {
      setSelectedStatus(null);
    } else {
      setSelectedStatus(value);
    }
    // Don't save immediately - let user choose action via the right side button
  };

  const handleRightButtonAction = () => {
    if (selectedStatus === taskStage && hasContent) {
      handleSave();
    } else if (selectedStatus && hasContent) {
      handleSave(selectedStatus);
    } else if (selectedStatus && !hasContent) {
      handleSave(selectedStatus);
    } else if (hasContent && !selectedStatus) {
      handleSave();
    }
  };

  const getRightButtonText = () => {
    if (selectedStatus === taskStage && hasContent) {
      return "Comment";
    } else if (selectedStatus && hasContent) {
      return "Comment and update status";
    } else if (selectedStatus && !hasContent) {
      return "Update status";
    } else if (hasContent && !selectedStatus) {
      return "Comment";
    } else {
      return "Comment";
    }
  };

  const isRightButtonDisabled = () => {
    return disabled || uploadsInProgress || (!hasContent && !selectedStatus);
  };

  return (
    <Box sx={{ display: "flex", gap: "20px" }}>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        <SimpleRichEditor
          disabled={disabled}
          placeholder={placeholder}
          setContent={setContent}
          setUploadsInProgress={setUploadsInProgress}
          triggerReset={triggerReset}
          onReset={() => {
            setContent(null);
            setTriggerReset(false);
          }}
          inputSx={{ borderRadius: "16px" }}
          initialContent={_initialContent}
          initialAttachments={initialAttachments}
        />
        <Box
          sx={{
            display: "flex",
            width: "100%",
            gap: "22px",
            justifyContent: isCreate ? "space-between" : "flex-start",
            alignItems: "center",
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: "12px",
              flexGrow: 1,
              justifyContent: isEditMode ? "flex-end" : "space-between",
              width: isMobile ? "100%" : undefined,
              flexDirection: isMobile ? "column-reverse" : "row",
            }}
          >
            {/* Task Assignments feature: External comment button */}
            <FlexBox stackedMobile justifyContent="end">
              <StageChoiceButton
                withComment={hasContent}
                currentStage={taskStage}
                accountType={accountType}
                disabled={disabled}
                dropdownButtonId={dropdownButtonId}
                onClick={handleStatusSelect}
              />
              <FlexBox sx={{ alignItems: "center", flex: 0 }}>
                <Box>
                  <Button
                    sx={{
                      height: "unset",
                      padding: "8px 18px",
                      whiteSpace: "nowrap",
                    }}
                    onClick={handleRightButtonAction}
                    disabled={isRightButtonDisabled()}
                  >
                    {getRightButtonText()}
                  </Button>
                </Box>
              </FlexBox>
            </FlexBox>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
